import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { useTranslation } from 'react-i18next';
import { POST_URL } from '@utils/api/endpoints.ts';

interface InvoiceBillablesPayload {
  ids: number[] | undefined | null;
}

// A 200 can still mean a purchaser was not invoiced, e.g. WeFact rejected
// its invoice. The API then names each one in `message`.
export interface InvoiceBillablesResponse {
  result: {
    invoices: unknown[];
    message: string | null;
  };
}

export const useInvoiceBillables = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invoiceBillables = async (payload: InvoiceBillablesPayload) => {
    try {
      const { data } = await Axios.post<InvoiceBillablesResponse>(
        `${POST_URL().billing}`,
        { ids: payload.ids }
      );
      return data;
    } catch (error) {
      if ((error as AxiosError<BaseError>).response) {
        throw error;
      }
      // No HTTP response — timeout, CORS, or offline. Invoicing a
      // large batch calls Wefact once per org and can take minutes;
      // the Axios 300s timeout can fire before the backend returns
      // even though the invoices WERE created. Tell the user to
      // refresh instead of pretending nothing happened (seen on 2026-10-03
      // when Frans invoiced ~13 orgs and saw this toast — invoices
      // did land but he had to refresh to see them).
      throw new Error(t('invoice-request-timed-out-refresh-to-check'));
    }
  };

  return useMutation<
    InvoiceBillablesResponse,
    AxiosError<BaseError>,
    InvoiceBillablesPayload
  >(invoiceBillables, {
    onSuccess: () => {
      queryClient.invalidateQueries('billablesBo');
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    useErrorBoundary: false
  });
};
